import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Plus, MoreVertical } from 'lucide-react';
import api from '../../utils/api';
import { fmtRange12 } from '../../utils/time';
import { ErrorState, PersonAvatar } from '../../components/ui';
import { useClientRecord, ClientFrame } from './ClientLayout';
import { fullName } from './clientShared';

const DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function getMonthGrid(year, month) {
  const firstDay = new Date(year, month, 1);
  const startDow  = (firstDay.getDay() + 6) % 7; // 0=Mon
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const prevDays = new Date(year, month, 0).getDate();
  const cells = [];
  for (let i = startDow - 1; i >= 0; i--) cells.push({ date: prevDays - i, current: false });
  for (let d = 1; d <= daysInMonth; d++) cells.push({ date: d, current: true, full: new Date(year, month, d) });
  const remaining = 42 - cells.length;
  for (let d = 1; d <= remaining; d++) cells.push({ date: d, current: false });
  return cells;
}

const CALENDAR_PALETTE = [
  'bg-green-100 text-green-800 border-l-2 border-green-400',
  'bg-blue-100 text-blue-800 border-l-2 border-blue-400',
  'bg-orange-100 text-orange-800 border-l-2 border-orange-400',
  'bg-purple-100 text-purple-800 border-l-2 border-purple-400',
  'bg-cyan-100 text-cyan-800 border-l-2 border-cyan-400',
  'bg-yellow-100 text-yellow-800 border-l-2 border-yellow-400',
  'bg-red-100 text-red-800 border-l-2 border-red-400',
  'bg-emerald-100 text-emerald-800 border-l-2 border-emerald-400',
];

function calendarColor(shiftType) {
  if (!shiftType) return 'bg-slate-100 text-slate-700 border-l-2 border-slate-400';
  let h = 0;
  for (let i = 0; i < shiftType.length; i++) h = ((h << 5) - h + shiftType.charCodeAt(i)) | 0;
  return CALENDAR_PALETTE[Math.abs(h) % CALENDAR_PALETTE.length];
}

export default function ClientCalendar() {
  const { id } = useParams();
  const navigate = useNavigate();
  const record = useClientRecord(id);
  const { client } = record;
  const [shifts, setShifts]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [now, setNow]         = useState(new Date());
  const datePickerRef         = useRef(null);
  const year  = now.getFullYear();
  const month = now.getMonth();
  const cells = getMonthGrid(year, month);

  useEffect(() => {
    setLoading(true); setError(null);
    api.get(`/clients/${id}/calendar?year=${year}&month=${month + 1}`)
      .then(r => setShifts(Array.isArray(r.data) ? r.data : []))
      .catch(e => { setShifts([]); setError(e.response?.data?.message || 'We couldn’t load this month’s shifts. Check your connection, then try again.'); })
      .finally(() => setLoading(false));
  }, [id, year, month, reloadKey]);

  const shiftsForDay = (d) => {
    if (!d) return [];
    const key = `${year}-${String(month + 1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
    return shifts.filter(s => s.shift_date === key);
  };

  const prev = () => setNow(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const next = () => setNow(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));
  const today = () => setNow(new Date());

  return (
    <ClientFrame record={record}>
      {/* Calendar toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        {client && (
          <div className="flex items-center gap-2">
            <PersonAvatar id={client.id} photo={client.profile_photo_url} firstName={client.first_name} lastName={client.last_name} size="xs" />
            <span className="text-sm font-medium text-slate-700">{fullName(client)}</span>
          </div>
        )}
        <div className="flex-1 text-center">
          <h2 className="text-base font-semibold text-slate-900">{MONTHS[month]} {year}</h2>
        </div>
        <div className="flex flex-wrap items-center gap-2 ml-auto">
          <label htmlFor="client-cal-month" className="text-[12.5px] text-slate-500">Month</label>
          <input
            id="client-cal-month"
            ref={datePickerRef}
            type="month"
            value={`${year}-${String(month + 1).padStart(2,'0')}`}
            onChange={e => {
              if (!e.target.value) return;
              const [y, m] = e.target.value.split('-').map(Number);
              setNow(new Date(y, m - 1, 1));
            }}
            className="h-9 border border-slate-200 rounded-lg px-2 text-sm cursor-pointer"
          />
          <button type="button" onClick={prev} aria-label="Previous month" className="w-9 h-9 inline-flex items-center justify-center border border-slate-200 rounded-lg hover:bg-slate-50"><ChevronLeft size={15} aria-hidden /></button>
          <button type="button" onClick={today} className="h-9 px-3 border border-slate-200 rounded-lg text-sm hover:bg-slate-50">This month</button>
          <button type="button" onClick={next} aria-label="Next month" className="w-9 h-9 inline-flex items-center justify-center border border-slate-200 rounded-lg hover:bg-slate-50"><ChevronRight size={15} aria-hidden /></button>
          <button type="button"
            onClick={() => navigate(`/scheduler?clientId=${id}`)}
            className="btn-primary">
            <Plus size={15} aria-hidden /> Add shift
          </button>
        </div>
      </div>

      {error && <div className="bg-white border border-slate-200 rounded-xl"><ErrorState message={error} onRetry={() => setReloadKey(k => k + 1)} /></div>}

      {/* Calendar grid: scrolls sideways on its own on narrow screens */}
      <div className="card overflow-x-auto" aria-busy={loading || undefined}>
        <div className="min-w-[44rem]">
        {/* Day headers */}
        <div className="grid grid-cols-7 border-b border-slate-200">
          {DAYS.map(d => (
            <div key={d} className="text-center py-2 text-xs font-semibold text-slate-600 border-r border-slate-100 last:border-0">
              {d}
            </div>
          ))}
        </div>

        {/* Cells */}
        <div className="grid grid-cols-7">
          {cells.map((cell, idx) => {
            const dayShifts = cell.current ? shiftsForDay(cell.date) : [];
            const isToday = cell.current && cell.full?.toDateString() === new Date().toDateString();
            return (
              <div key={idx} className={`min-h-[120px] border-r border-b border-slate-100 last:border-r-0 p-1 ${
                !cell.current ? 'bg-slate-50/50' : isToday ? 'bg-blue-50/30' : ''
              }`}>
                <div className={`text-xs font-medium mb-1 w-6 h-6 flex items-center justify-center rounded-full ${
                  isToday ? 'bg-primary-600 text-white' : cell.current ? 'text-slate-700' : 'text-slate-400'
                }`}>
                  {cell.date}
                </div>
                <div className="space-y-0.5">
                  {dayShifts.slice(0, 3).map((s, i) => {
                    const colorClass = calendarColor(s.shift_type);
                    const dateStr = `${year}-${String(month + 1).padStart(2,'0')}-${String(cell.date).padStart(2,'0')}`;
                    return (
                      <button type="button" key={i}
                        onClick={() => navigate(`/scheduler?date=${dateStr}`)}
                        aria-label={`${fmtRange12(s.start_time, s.end_time)}, ${s.staff_name || 'unassigned'}. Open in the scheduler`}
                        className={`block w-full text-left text-[11px] rounded px-1 py-0.5 leading-tight transition-opacity hover:opacity-80 ${colorClass}`}>
                        <div className="font-medium">{fmtRange12(s.start_time, s.end_time)}</div>
                        <div className="truncate">{s.staff_name || 'Unassigned'}</div>
                      </button>
                    );
                  })}
                  {dayShifts.length > 3 && (
                    <div className="text-[11px] text-slate-500 pl-1">+{dayShifts.length - 3} more</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        </div>
      </div>
    </ClientFrame>
  );
}
